// import AppealsPage from '@/app/pages/appeals/Page.tsx'
import EmailVerification from '@/app/pages/authentication/email_verified/Page.tsx'
import { TwoFAuth } from '@/app/pages/authentication/login/TwoFAuth'
import { LoginTest } from '@/app/pages/authentication/login_test/Page'
import { ProtectedRoute } from '@/app/pages/authentication/ProtectedRoute'
import RegistrationPage from '@/app/pages/authentication/registration/Page.tsx'
import RegistrationInvite from '@/app/pages/authentication/registration/RegistrationInvite.tsx'
import DashboardPage from '@/app/pages/dashboard/Page'
import DocumentsPage from '@/app/pages/documents/Page'
import MaintenancePage from '@/app/pages/maintenance/Page'
import ItemDetailPage from '@/app/pages/marketplace/marketplace-live/ItemDetailPage.tsx'
import ProviderDetail from '@/app/pages/marketplace/marketplace-live/providers/ProviderDetail.tsx'
import { MarketplacePreviewItemDetailsPage } from '@/app/pages/marketplace/marketplace-preview/item-details/Page.tsx'
import MarketplacePreviewLayout from '@/app/pages/marketplace/marketplace-preview/MarketplacePreviewLayout.tsx'
import { MarketplacePreviewPage } from '@/app/pages/marketplace/marketplace-preview/Page.tsx'
import MarketplacePreviewProviderDetailsPage from '@/app/pages/marketplace/marketplace-preview/provider_details/Page.tsx'
import MessagesPage from '@/app/pages/messages/Page.tsx'
import { PaymentAppealPage } from '@/app/pages/payments/pages/payment_appeal/payment_appeal.page.tsx'
import PaymentDetailsPage from '@/app/pages/payments/pages/payment_details/payment_details.page.tsx'
import PrivacyStatementPage from '@/app/pages/privacy-statement/Page.tsx'
import { CreateProviderRequestPage } from '@/app/pages/providers/create_provider_request_page.tsx'
import { ReimbursementAccountsPage } from '@/app/pages/reimbursements/accounts/Page'
import { CreateReimbursementPage } from '@/app/pages/reimbursements/new/pages/create_reimbursement_page.tsx'
import V1toV2BridgePage from '@/app/pages/renewal_bridge/Page.tsx'
import SSOLogoutPage from '@/app/pages/sso_logout/Page'
import TermsAndConditionsPage from '@/app/pages/terms-and-conditions/Page.tsx'
import TransactionsPage from '@/app/pages/transactions/Page'
import TransactionDetailsPage from '@/app/pages/transactions/TransactionDetails'
import RefreshApplication from '@/app/RefreshApplication.tsx'
import { SchoolYearProvider } from '@/app/SchoolYearProvider.tsx'
import AuthSessionMonitor from '@/app/services/AuthSessionMonitor.tsx'
import { ProgramSettingsService } from '@/app/services/ProgramSettingsFetcher.tsx'
import { LayoutProvider } from '@/layouts/LayoutProvider.tsx'
import { ReimbursementAccountLayout } from '@/layouts/reimbursements/account/ReimbursementAccountLayout'
import { APPLICATION_ROUTES as FamilyApplicationRoutes } from '@/routing/family_application_routes.tsx'
import { REIMBURSEMENT_ACCOUNT_ROUTES as ReimbursementAccountRoutes } from '@/routing/reimbursements_routes.tsx'
import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom'
import App from '../App.tsx'
import CheckEmailPage from '../app/pages/authentication/forgot_password/check_email/Page.tsx'
import EnterEmailPage from '../app/pages/authentication/forgot_password/enter_email/Page.tsx'
import LoginPage from '../app/pages/authentication/login/Page.tsx'
import ResetPasswordPage from '../app/pages/authentication/reset_password/Page.tsx'
import FamilyApplicationRenewalPage from '../app/pages/family_application_renewal/Page.tsx'
import MarketplacePage from '../app/pages/marketplace/marketplace-live/Page.tsx'
import MyAccountPage from '../app/pages/my_account/Page.tsx'
import PaymentsPage from '../app/pages/payments/pages/payments-list/payments_list.page.tsx'
import LandingPage from '../app/pages/program_applications/vendor_application/LandingPage.tsx'
import WizardLayout from '../app/pages/program_applications/vendor_application/Layout.tsx'
import VendorApplicationChecklist from '../app/pages/program_applications/vendor_application/VendorApplicationChecklist.tsx'
import BasicInfo from '../app/pages/program_applications/vendor_application/wizard/BasicInfo.tsx'
import BusinessInfo from '../app/pages/program_applications/vendor_application/wizard/BusinessInfo.tsx'
import ConsentReview from '../app/pages/program_applications/vendor_application/wizard/ConsentReview.tsx'
import LocationDeclaration from '../app/pages/program_applications/vendor_application/wizard/LocationDeclaration.tsx'
import POCInfo from '../app/pages/program_applications/vendor_application/wizard/POCInfo.tsx'
import SupportingDocuments from '../app/pages/program_applications/vendor_application/wizard/SupportingDocuments.tsx'
import { WizardProvider } from '../app/pages/program_applications/vendor_application/WizardProvider.tsx'
import '../i18n'
import { MaintenanceWrapper } from '../middleware/maintenanceMiddleware'
import { AppRoutePaths, PROVIDER_APPLICATION_URLS } from './route_paths.ts'
import AffiliationDetailPage from '../app/pages/marketplace/marketplace-live/affiliations/AffiliationDetailPage.tsx'

const ProgramWizardPaths = PROVIDER_APPLICATION_URLS.wizard

import { NoAccessPage } from '@/app/pages/authentication/NoAccessPage.tsx'

const AUTH_ROUTES: RouteObject[] = [
  {
    path: '/',
    element: <Navigate to={AppRoutePaths.dashboard.index} replace />, // Redirect to home dashboard
  },
  {
    path: '/',
    element: <ProtectedRoute />,
    children: [
      {
        path: AppRoutePaths.auth.login,
        element: <LoginPage />,
        index: true,
      },
      {
        path: AppRoutePaths.auth.twofa,
        element: <TwoFAuth />,
        index: true,
      },
      {
        path: AppRoutePaths.auth.forgot_password.index,
        children: [
          {
            path: '',
            element: <EnterEmailPage />,
          },
          {
            path: 'enter_email',
            element: <EnterEmailPage />,
          },
          {
            path: 'check_email',
            element: <CheckEmailPage />,
          },
        ],
      },
      {
        path: AppRoutePaths.auth.reset_password,
        element: <ResetPasswordPage />,
      },
      {
        path: AppRoutePaths.auth.email_verification,
        element: <EmailVerification />,
      },
      {
        path: AppRoutePaths.auth.register,
        element: <RegistrationPage />,
      },
      {
        path: AppRoutePaths.auth.registration_invite,
        element: <RegistrationInvite />,
      },
      {
        path: AppRoutePaths.auth.no_access,
        element: <NoAccessPage />,
      },
    ],
  },
]

const PUBLIC_ROUTES: RouteObject[] = [
  {
    path: '/',
    children: [
      {
        path: AppRoutePaths.renewal_bridge.index,
        element: <V1toV2BridgePage />,
      },
      {
        path: AppRoutePaths.privacy_statement.index,
        element: <PrivacyStatementPage />,
      },
      {
        path: AppRoutePaths.termsAndConditions.index,
        element: <TermsAndConditionsPage />,
      },
      {
        path: AppRoutePaths.sso_logout.index,
        element: <SSOLogoutPage />,
      },
      {
        path: AppRoutePaths.marketplace_preview.index,
        element: <MarketplacePreviewLayout />,
        children: [
          {
            index: true,
            element: <MarketplacePreviewPage />,
          },
          {
            path: AppRoutePaths.marketplace_preview.details,
            element: <MarketplacePreviewItemDetailsPage />,
          },
          {
            path: AppRoutePaths.marketplace_preview.provider_details,
            element: <MarketplacePreviewProviderDetailsPage />,
          },
          {
            path: AppRoutePaths.marketplace_preview.details,
            element: <MarketplacePreviewItemDetailsPage />,
          },
        ],
      },
    ],
  },
] as const

const PRIVATE_ROUTES: RouteObject[] = [
  {
    path: '/',
    element: (
      <LayoutProvider>
        <AuthSessionMonitor>
          <SchoolYearProvider>
            <ProgramSettingsService>
              <RefreshApplication>
                <App />
              </RefreshApplication>
            </ProgramSettingsService>
          </SchoolYearProvider>
        </AuthSessionMonitor>
      </LayoutProvider>
    ),
    children: [
      {
        path: AppRoutePaths.family_application_renewal.index,
        element: <FamilyApplicationRenewalPage />,
      },
      {
        path: AppRoutePaths.family_application.index,
        element: <Navigate to={AppRoutePaths.dashboard.index} replace />,
        children: FamilyApplicationRoutes,
      },
      {
        path: AppRoutePaths.providers.index,
        element: <CreateProviderRequestPage />,
      },
      {
        path: AppRoutePaths.reimbursements.accounts.index,
        element: <ReimbursementAccountsPage />,
      },

      {
        path: AppRoutePaths.reimbursements.accounts.link.index,
        element: <ReimbursementAccountLayout />,
        children: ReimbursementAccountRoutes,
      },
      {
        path: AppRoutePaths.payments_appeals.id,
        element: <PaymentAppealPage />,
      },
      {
        path: AppRoutePaths.marketplace.index,
        element: <MarketplacePage />,
        handle: {
          page_details: {
            title: 'pages.marketplace.title',
            description: 'pages.marketplace.description',
          },
        },
      },
      {
        path: AppRoutePaths.marketplace.cart.index,
        element: <MarketplacePage />,
        handle: {
          page_details: {
            title: 'pages.marketplace.title',
            description: 'pages.marketplace.description',
          },
        },
      },
      {
        path: AppRoutePaths.marketplace.details.index,
        element: <ItemDetailPage />,
        handle: {
          page_details: {
            title: 'pages.marketplace.title',
            description: 'pages.marketplace.description',
          },
        },
      },
      {
        path: AppRoutePaths.marketplace.provider.index,
        element: <ProviderDetail />,
      },
      {
        path: AppRoutePaths.marketplace.affiliation.index,
        element: <AffiliationDetailPage />,
      },
      {
        path: AppRoutePaths.messages.index,
        element: <MessagesPage />,
      },
      {
        path: AppRoutePaths.documents.index,
        element: <DocumentsPage />,
      },
      {
        path: AppRoutePaths.dashboard.index,
        element: <DashboardPage />,
      },
      {
        path: AppRoutePaths.home.index,
        element: <Navigate to={AppRoutePaths.dashboard.index} replace />,
      },
      {
        path: AppRoutePaths.payments.index,
        element: <PaymentsPage />,
      },

      {
        path: AppRoutePaths.payments.details.index,
        element: <PaymentDetailsPage />,
      },
      {
        path: AppRoutePaths.reimbursements.new_reimbursement,
        element: <CreateReimbursementPage />,
      },
      {
        path: AppRoutePaths.bills.details.index,
        element: <PaymentDetailsPage />,
      },
      {
        path: AppRoutePaths.transactions.index,
        element: <TransactionsPage />,
      },
      {
        path: AppRoutePaths.transactions.details.index,
        element: <TransactionDetailsPage />,
      },
      /*   {
      /*   {
        path: AppRoutePaths.appeals.ineligeble_appeal.application,
        element: <ApplicationAppealPage />,
      },
      {
        path: AppRoutePaths.appeals.ineligeble_appeal.priority,
        element: <ApplicationPriorityAppealPage />,
      }, */
      /*   {
        path: AppRoutePaths.reimbursements.index,
        element: <ReimbursementsPage />,
        handle: {
          page_details: {
            title: 'pages.reimbursements.title',
            description: 'pages.reimbursements.description',
          },
        },
      }, */
      {
        path: AppRoutePaths.assessments.index,
        element: <div></div>,
        handle: {
          page_details: {
            title: 'pages.assessments.title',
          },
        },
      },
      {
        path: AppRoutePaths.notifications.index,
        element: <Navigate to={AppRoutePaths.dashboard.index} replace />,
        handle: {
          page_details: {
            title: 'pages.notifications.title',
            description: 'pages.notifications.description',
          },
        },
      },
      {
        path: AppRoutePaths.my_account.index,
        element: <MyAccountPage />,
      },
      /*       {
        path: AppRoutePaths.appeals.index,
        element: <OpenAppealsView />,
        handle: {
          page_details: {
            title: 'pages.appeals.title',
            description: 'pages.appeals.description',
          },
        },

      },
      {
        path: AppRoutePaths.appeals.id,
        element: <ApplicationAppealDetailPage />,
      }, */
    ],
  },

  {
    //ROUTES - program_applications/basic...
    path: AppRoutePaths.program_application.index,
    element: <Navigate to={AppRoutePaths.dashboard.index} replace />,
    children: [
      {
        index: true,
        element: <LandingPage />,
      },
      {
        path: '/program_application/wizard',
        element: (
          <WizardProvider>
            <WizardLayout />
          </WizardProvider>
        ),
        children: [
          {
            index: true,
            element: <VendorApplicationChecklist />,
          },
          {
            path: ProgramWizardPaths.location_declaration.index,
            element: <LocationDeclaration />,
          },
          {
            path: ProgramWizardPaths.basic_info.index,
            element: <BasicInfo />,
          },
          {
            path: ProgramWizardPaths.business_info.index,
            element: <BusinessInfo />,
          },
          {
            path: ProgramWizardPaths.poc_info.index,
            element: <POCInfo />,
          },
          {
            path: ProgramWizardPaths.consent_review.index,
            element: <ConsentReview />,
          },
          {
            path: ProgramWizardPaths.service_info.index,
            element: <SupportingDocuments />,
          },
        ],
      },
    ],
  },
]

export const router = createBrowserRouter([
  {
    path: AppRoutePaths.maintenance.index,
    element: <MaintenancePage />,
  },
  {
    path: AppRoutePaths.auth.login_test,
    element: <LoginTest />,
  },
  {
    element: <MaintenanceWrapper />,
    children: [...AUTH_ROUTES, ...PUBLIC_ROUTES, ...PRIVATE_ROUTES],
  },
])
